import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { signInAdmin } from "@/lib/supabaseClient";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_thunder-alert-maps/artifacts/6okjv5b7_UKWX%20LOGO%20NEW%202025.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInAdmin(email, password);
      toast.success("Login successful");
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Invalid credentials");
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0B1220] flex items-center justify-center px-4"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1763488448321-659df848cd3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZHJhbWF0aWMlMjB0aHVuZGVyc3Rvcm0lMjBjbG91ZHMlMjBhdG1vc3BoZXJpY3xlbnwwfHx8fDE3NzE1OTg0Njd8MA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 bg-[#0B1220]/80 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="UKWX Logo" className="h-16 w-auto" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#F9FAFB] uppercase tracking-wider">
            Admin Portal
          </h1>
          <p className="text-[#9CA3AF] text-sm mt-2">UKWX Outlooks</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="bg-[#111827]/90 border border-white/10 p-8 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#0B1220] border-white/10 rounded-none font-mono text-sm focus:border-[#38BDF8]"
                  placeholder="Enter email"
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#0B1220] border-white/10 rounded-none font-mono text-sm focus:border-[#38BDF8]"
                  placeholder="Enter password"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-[#F33D14]/10 border border-[#F33D14]/30 text-[#F33D14] text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#38BDF8] text-[#0B1220] hover:bg-[#0EA5E9] font-bold uppercase tracking-wider rounded-none py-3"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-[#0B1220] border-t-transparent rounded-full animate-spin" />
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-xs text-[#6B7280] hover:text-[#9CA3AF] uppercase tracking-wider transition-colors"
          >
            ← Back to Public Site
          </a>
        </div>
      </div>
    </div>
  );
}
